import { useMemo } from "react";
import AddressLable from "../components/Enrollment/AddressLable"; // <-- typo fixed
import DatePicker from "react-datepicker";
import { isArray } from "lodash";

const safeDate = (v) => {
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d; // null = nothing selected
};

const splitTitleDesc = (title = "") => {
  const m = title.match(/^(.*?)(?:\s*\((.+)\))?$/);
  return { label: (m?.[1] || "").trim(), desc: (m?.[2] || "").trim() };
};

const getTitle = (field) => {
  if (field?.title && field.title.trim()) return field.title;
  if (field?.id === 160) return "Owner Type";
  return "";
};

const toggleTooltip = (fieldID, hasTooltip, setInfoShowing) => {
  if (!hasTooltip) return;
  setInfoShowing((prev) => {
    const next = { ...prev, [fieldID]: !prev[fieldID] };
    if (!prev[fieldID]) {
      setTimeout(
        () => setInfoShowing((p) => ({ ...p, [fieldID]: false })),
        50000
      );
    }
    return next;
  });
};

const HALF_WIDTH_IDS = new Set([
  120, 121, 122, 123, 126, 127, 158, 159, 162, 163,
]);
const isHalfByTitle = () => false;

const FieldLabel = ({ field, setInfoShowing, infoShowing }) => {
  const id = String(field?.id ?? field?.fieldId ?? "");
  const title = getTitle(field);
  const showAsterisk = !!title && !!field?.isMandatory;

  // API key is "toolTip" (capital T)
  const hasTip = field?.toolTip && String(field.toolTip).trim().length > 0;

  const isOpen = !!(infoShowing && infoShowing[id]);

  const toggle = () => {
    if (!setInfoShowing) return;
    setInfoShowing((prev) => ({
      ...(prev || {}),
      [id]: !isOpen,
    }));
  };

  return (
    <div className="form-label">
      {title && (
        <div className="label-val">
          {title} {showAsterisk && <span className="mandatory-star">*</span>}
        </div>
      )}

      {hasTip && (
        <span className="tooltip-anchor">
          <button
            type="button"
            className={`info-Img ${isOpen ? "is-open" : ""}`}
            onClick={toggle}
            aria-label="Info"
          >
            {isOpen ? (
              <img src="/Images/info-fill.png" alt="Info" />
            ) : (
              <img src="/Images/info.png" alt="Info" />
            )}
          </button>

          {isOpen && (
            <div className="info-text">
              <span className="info-text__title">{title || "Info"}:</span>
              <span
                className="info-text__content"
                // Allow HTML coming from API (e.g., <b>...</b>)
                dangerouslySetInnerHTML={{ __html: String(field.toolTip) }}
              />
            </div>
          )}
        </span>
      )}
    </div>
  );
};

const renderField = ({
  allcountries,
  field,
  formData,
  removeShareholder,
  errors,
  setErrors,
  setFormData,
  handleInputChange,
  setModalMode,
  setModalTitle,
  setModalDes,
  setModalShow,
  infoShowing,
  setInfoShowing,
  shareholder,
  setShareHolder,
  setModalPayload,
  openCountryModal,
  handleUploadChange,
  openCountrySlot,
  outsideSelections = {},
  setOutsideSelections,
  fieldsIndex,
  extraOwners,
  setExtraOwners,
  addShareholder,
  countries,
  ownerIdxMap,
  groupOrdinal = 1,
  suppressPrefill = false,
  message,
  renderGuard,
  setOwnerIdxMap,
  setmodifiedData,
  groupParentId,
}) => {

  //   const fieldID = field.id;
  const fieldID = String(field.id ?? field.fieldId);
  function baseId(id) {
    return Number(String(id).split("_")[0]);
  }

  const isPadded = field?.isPadded ?? false;

  const EXCLUDE_IN_OWNER = new Set([160]);

  const _base = baseId(fieldID);
  const _guard = renderGuard || new Set();
  if (_guard.has(_base)) return null;
  const _nextGuard = new Set(_guard);
  _nextGuard.add(_base);

  //   const errorDisplay = (
  //     <div className="error" id={`error-${fieldID}`}>
  //       {errors[fieldID]}
  //     </div>
  //   );

  const renderError = (key) =>
    errors?.[key] ? (
      <div className="error" id={`error-${key}`}>
        {errors[key]}
      </div>
    ) : (
      <div className="error" id={`error-${key}`}></div>
    );

  const isReadOnly = field?.isDisabled ?? false;

  function baseId(id) {
    return Number(String(id).split("_")[0]); // "120_2" → 120
  }

  const keyName =
    groupOrdinal && groupOrdinal > 1
      ? `${fieldID}_${groupOrdinal - 1}`
      : fieldID;

  const isHalf = HALF_WIDTH_IDS.has(baseId(fieldID)) || isHalfByTitle(field);
  //   const isHalf = HALF_WIDTH_IDS.has(Number(fieldID)) || isHalfByTitle(field);

  const linkedList = field?.linkedFields.length > 0;

  if (field.isNestedField) {
    const baseNum = Number(String(fieldID).split("_")[0]); // e.g., "187_2" -> 187
    let allowed = false;

    if (fieldsIndex) {
      // Only consider radios (type 5) that actually control THIS field id,
      // and read the radio value from the SAME group (suffix-aware).
      outer: for (const p of Object.values(fieldsIndex)) {
        if (Number(p?.type) !== 5) continue;
        const options = p.options || [];
        const controlsThis = options.some((o) =>
          (o.nestedFields || []).some((id) => Number(id) === baseNum)
        );
        if (!controlsThis) continue;

        // suffix-aware key for the controlling radio
        const rKey =
          groupOrdinal && groupOrdinal > 1
            ? `${p.id}_${groupOrdinal - 1}`
            : String(p.id);

        const sel = String(formData[rKey] ?? "");
        if (!sel) continue;

        const opt = options.find((o) => String(o.optionId) === sel);
        if (opt?.nestedFields?.some((id) => Number(id) === baseNum)) {
          allowed = true;
          break outer;
        }
      }
    }

    if (!allowed) return null; // do not render nested child if its group's radio doesn't select it
  }

  switch (field.type) {
    case 1:
    case 13: {
      const isEmail = field.type === 13;
      const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

      const showErr = errors?.[keyName];
      const errorDisplay = showErr ? (
        <div className="error">{showErr}</div>
      ) : null;

      return (
        <div
          className={`form-group ${isHalf ? "form-group--half" : ""} 
	      ${isPadded ? "ms-3" : ""}`}
        >
          <FieldLabel
            field={field}
            setInfoShowing={setInfoShowing}
            infoShowing={infoShowing}
          />

          <input
            className={`form-input input ${isHalf ? "input--full" : ""}`}
            disabled={isReadOnly}
            type={isEmail ? "email" : "text"}
            placeholder={field.placeHolder || ""}
            id={keyName}
            name={keyName}
            value={formData[keyName] || ""}
            onChange={handleInputChange}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (isEmail) {
                if (v && !EMAIL_RE.test(v)) {
                  setErrors((prev) => ({
                    ...prev,
                    [keyName]: "Please enter a valid email address.",
                  }));
                } else {
                  setErrors((prev) => {
                    const copy = { ...prev };
                    delete copy[keyName];
                    return copy;
                  });
                }
              }
            }}
            // lets the browser assist on mobile & adds native validation too
            inputMode={isEmail ? "email" : undefined}
            autoComplete={isEmail ? "email" : undefined}
            pattern={isEmail ? EMAIL_RE.source : undefined}
            maxLength={field.length || 255}
          />

          {renderError(keyName)}
        </div>
      );
    }

    case 2: {
      return (
        <div
          className={`form-group ${isHalf ? "form-group--half" : ""} ${
            isPadded ? "ms-3" : ""
          }`}
        >
          <FieldLabel
            field={field}
            setInfoShowing={setInfoShowing}
            infoShowing={infoShowing}
          />
          <input
            className="form-input input"
            disabled={isReadOnly}
            type="text"
            placeholder={field.placeHolder || ""}
            // no locale formatting; keep raw string
            value={formData[keyName] ?? ""}
            onChange={(e) => {
              // allow only digits, slash, underscore, hyphen
              const raw = e.target.value.replace(/[^0-9#\/_\\-]/g, "");

              setFormData((prev) => ({ ...prev, [keyName]: raw }));
              setmodifiedData?.((prev) => ({ ...prev, [keyName]: raw }));
              setErrors?.((prev) =>
                prev?.[keyName] ? { ...prev, [keyName]: null } : prev
              );
            }}
            onKeyDown={(e) => {
              // still block scientific notation and plus sign; allow '-','/','_'
              if (["e", "E", "+"].includes(e.key)) e.preventDefault();
            }}
            // numeric keyboard isn't ideal when allowing symbols; "tel" or "text" works better
            inputMode="tel"
            maxLength={field.length || 20}
            // pattern that matches only allowed characters
            pattern="[0-9#\/_\\-]*"
          />
          {renderError(keyName)}
        </div>
      );
    }

    case 5: {
      // Build radio options with their nestedFields
      const opts = (field.options || []).map((o) => ({
        value: String(o.optionId),
        label: o.name,
        nestedFields: o.nestedFields || [],
      }));

      // Radio state uses keyName so clones (groups) are independent
      const selected = String(formData[keyName] ?? "");

      // Compute which nested fields to render for the selected option
      const chosenOpt = opts.find((o) => o.value === selected);
      let nestedIds = chosenOpt?.nestedFields
        ? [...chosenOpt.nestedFields]
        : [];

      // If a type-22 parent is among nestedIds, DO NOT inline its own children here.
      // (That parent will render its children inside.)
      if (Array.isArray(nestedIds) && fieldsIndex) {
        const parents22 = nestedIds.filter(
          (id) => fieldsIndex?.[Number(id)]?.type === 22
        );
        if (parents22.length) {
          const childrenToDrop = new Set(
            parents22.flatMap(
              (pid) => fieldsIndex?.[Number(pid)]?.linkedFields || []
            )
          );
          nestedIds = nestedIds.filter((id) => !childrenToDrop.has(Number(id)));
        }
      }

      const nestedObjs = (nestedIds || [])
        .map((id) => fieldsIndex?.[Number(id)])
        .filter(Boolean);

      // Current group's suffix: "" for group 1, or "_1", "_2", ... for clones
      const currentSuffix =
        groupOrdinal && groupOrdinal > 1 ? `_${groupOrdinal - 1}` : "";

      // When rendering the owner type (field 160) inside a repeater (parent 22),
      // if a value is already selected for the first group, the radio will be prefilled but still editable.
      // This flag is no longer used to disable the first radio.
      const disableInitialRadio = false;

      return (
        <div className={`form-group ${isPadded ? "ms-3" : ""}`}>
          <FieldLabel
            field={field}
            setInfoShowing={setInfoShowing}
            infoShowing={infoShowing}
          />

          <div
            className="radio-group mt-4"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              marginBottom: "2rem",
            }}
          >
            {opts.map((o) => (
              <label key={o.value} className="radio-cont">
                <span
                  className={`radio-pill ${
                    selected === o.value ? "is-checked" : ""
                  }`}
                >
                  <span>{o.label}</span>
                  <input
                    type="radio"
                    name={keyName}
                    value={o.value}
                    checked={selected === o.value}
                    disabled={isReadOnly}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      const prev = String(formData[keyName] ?? "");

                      // ----- CLEANUP the previously selected branch *only for THIS group* -----
                      if (
                        prev &&
                        prev !== newVal &&
                        Array.isArray(field.options)
                      ) {
                        const prevOpt =
                          field.options.find(
                            (x) => String(x.optionId) === prev
                          ) || null;

                        // Base IDs to wipe (from prev branch)
                        const idsToWipe = new Set(prevOpt?.nestedFields || []);

                        // If prev branch had a type-22 parent, also wipe its linked children
                        (prevOpt?.nestedFields || []).forEach((nid) => {
                          const nf = fieldsIndex?.[Number(nid)];
                          if (
                            nf?.type === 22 &&
                            Array.isArray(nf.linkedFields)
                          ) {
                            nf.linkedFields.forEach((cid) =>
                              idsToWipe.add(Number(cid))
                            );
                          }
                        });

                        // Helper: remove keys that match both base id AND current group suffix
                        const wipeGroupKeys = (obj) => {
                          if (!obj) return obj;
                          const copy = { ...(obj || {}) };
                          Object.keys(copy).forEach((k) => {
                            const [baseStr, sufStr] = String(k).split("_");
                            const base = Number(baseStr);
                            const keySuffix = sufStr ? `_${sufStr}` : "";
                            const sameGroup = currentSuffix
                              ? keySuffix === currentSuffix
                              : keySuffix === "";
                            if (idsToWipe.has(base) && sameGroup)
                              delete copy[k];
                          });
                          return copy;
                        };

                        // Apply cleanup to formData / modifiedData / errors
                        setFormData?.((p) => wipeGroupKeys(p));
                        setErrors?.((p) => wipeGroupKeys(p));
                        setmodifiedData?.((p) => wipeGroupKeys(p));

                        // outsideSelections cleanup for THIS radio instance only
                        setOutsideSelections?.((p) => {
                          const copy = { ...(p || {}) };
                          Object.keys(copy).forEach((k) => {
                            const [baseStr, sufStr] = String(k).split("_");
                            const base = Number(baseStr);
                            const keySuffix = sufStr ? `_${sufStr}` : "";
                            const sameGroup = currentSuffix
                              ? keySuffix === currentSuffix
                              : keySuffix === "";
                            if (idsToWipe.has(base) && sameGroup)
                              delete copy[k];
                          });
                          return copy;
                        });

                        // If a wiped field was a type-22 parent, drop its clone suffixes too
                        if (typeof setOwnerIdxMap === "function") {
                          const wipedParents = [...idsToWipe].filter(
                            (id) => fieldsIndex?.[id]?.type === 22
                          );
                          if (wipedParents.length) {
                            setOwnerIdxMap((m) => {
                              const nm = { ...(m || {}) };
                              wipedParents.forEach((pid) => {
                                nm[pid] = []; // reset clones for that parent
                              });
                              return nm;
                            });
                          }
                        }
                      }

                      // finally set the new radio value
                      handleInputChange({
                        target: { name: keyName, value: newVal },
                      });
                    }}
                  />
                </span>
              </label>
            ))}

            {/* {Number(groupOrdinal) > 1 &&
              typeof removeShareholder === "function" &&
              typeof groupParentId === "number" && (
                <button
                  type="button"
                  className="btn-remove-group remove-section-btn"
                  onClick={() =>
                    removeShareholder(
                      Number(groupParentId),
                      Number(groupOrdinal) - 1
                    )
                  }
                  aria-label="Remove shareholder"
                  style={{ marginLeft: "auto" }}
                >
                  <span>−</span>
                </button>
              )} */}
          </div>

          {/* Render nested fields for the chosen option */}
          {nestedObjs.map((lf) =>
            renderField({
              allcountries,
              field: { ...lf, isNestedField: false },
              insideOwnerGroup: true,
              formData,
              errors,
              setErrors,
              setFormData,
              handleInputChange,
              setModalMode,
              setModalTitle,
              setModalDes,
              setModalShow,
              infoShowing,
              setInfoShowing,
              fieldsIndex,
              removeShareholder,
              // needed if a nested field is itself a type-22 or type-6
              ownerIdxMap,
              setOwnerIdxMap,
              addShareholder,
              setmodifiedData,
              countries,
              message,
              outsideSelections,
              setOutsideSelections,
              renderGuard: _nextGuard,
              groupOrdinal,
            })
          )}

          {renderError(keyName)}
        </div>
      );
    }

    case 6: {
      const opts = (field.options || []).map((o) => ({
        value: String(o.optionId),
        label: o.name,
        nestedFields: o.nestedFields || [],
      }));
      const outsideOpt = (field.options || []).find((o) =>
        /outside/i.test(o.name)
      );

      // radio state uses keyName so clones are independent
      const selected = String(formData[keyName] ?? "");
      const onSelect = (e) => {
        const prev = String(formData[keyName] ?? "");
        const newVal = e.target.value;
        handleInputChange({ target: { name: keyName, value: newVal } });

        // If switching Outside(2) -> Inside(1), clear nested field for THIS group + its 3 slots
        if (prev === "2" && newVal === "1") {
          const baseTarget = field?.options?.[1]?.nestedFields?.[0]; // usually 179
          if (baseTarget != null) {
            const ordinal = Number(groupOrdinal ?? 1);
            const targetKey =
              ordinal === 1
                ? String(baseTarget)
                : `${baseTarget}_${ordinal - 1}`;

            setFormData?.((p) => {
              const c = { ...(p || {}) };
              delete c[targetKey];
              return c;
            });
            setmodifiedData?.((p) => {
              const c = { ...(p || {}) };
              delete c[targetKey];
              return c;
            });
            setErrors?.((p) => {
              const c = { ...(p || {}) };
              delete c[targetKey];
              return c;
            });
          }
          // clear UI picks for this radio instance only
          setOutsideSelections?.((prevSel) => ({
            ...(prevSel || {}),
            [keyName]: [null, null, null],
          }));
        }
      };

      const isOutsideSelected =
        outsideOpt && selected === String(outsideOpt.optionId);

      // UI slots for this radio instance: API-first, then user picks
      const slots = outsideSelections[keyName] || [null, null, null];

      // normalize any API value -> array of numeric IDs
      const normalizeIds = (v) => {
        if (Array.isArray(v))
          return v.map((x) => Number(x)).filter((x) => !Number.isNaN(x));
        if (typeof v === "string" && v.trim()) {
          return v
            .split(",")
            .map((s) => Number(s.trim()))
            .filter((x) => !Number.isNaN(x));
        }
        if (v == null || v === "") return [];
        const n = Number(v);
        return Number.isNaN(n) ? [] : [n];
      };

      // nested field key for THIS group: 179, 179_1, 179_2, ...
      const baseTarget = field?.options?.[1]?.nestedFields?.[0]; // usually 179
      const ordinal = Number(groupOrdinal ?? 1);
      const targetKey =
        ordinal === 1 ? String(baseTarget) : `${baseTarget}_${ordinal - 1}`;
      const apiIds = normalizeIds(formData?.[targetKey]);

      return (
        <div className={`form-group ${isPadded ? "ms-3" : ""}`}>
          <FieldLabel
            field={field}
            setInfoShowing={setInfoShowing}
            infoShowing={infoShowing}
          />

          <div className="radio-group input-group my-3">
            {opts.map((opt) => {
              const checked = selected === opt.value;
              return (
                <label
                  key={`${fieldID}-${opt.value}`}
                  htmlFor={`${fieldID}-${opt.value}`}
                  className="radio-cont"
                  style={{ padding: "0" }}
                >
                  <span className={`radio-pill ${checked ? "is-checked" : ""}`}>
                    <input
                      type="radio"
                      id={`${fieldID}-${opt.value}`}
                      name={keyName}
                      value={opt.value}
                      checked={checked}
                      onChange={onSelect}
                      disabled={isReadOnly}
                    />
                    <span className="radio-label">{opt.label}</span>
                  </span>

                  {/* show 3 country slots only when Outside is selected */}
                  {`${fieldID}-${opt.value}` === `${fieldID}-2` &&
                    isOutsideSelected && (
                      <div className="country-block">
                        <div className="country-block__label">
                          Country of Transfers / Origin of Funds:
                        </div>

                        {[0, 1, 2].map((i) => {
                          const apiId = apiIds?.[i];
                          const apiCountry = countries?.find(
                            (c) => c.id === Number(apiId)
                          );
                          const picked = slots[i]; // { id, name } from user
                          const displayName =
                            apiCountry?.name ||
                            picked?.name ||
                            `Country (${i + 1})`;

                          return (
                            <button
                              key={`${fieldID}-country-${i}`}
                              type="button"
                              className="country-picker"
                              onClick={() => openCountrySlot(targetKey, i)} // suffixed key per group
                            >
                              <span
                                className={`country-picker__text ${
                                  apiCountry || picked ? "" : "is-placeholder"
                                }`}
                              >
                                {displayName}
                              </span>
                              <span
                                className="country-picker__chevron"
                                aria-hidden
                              >
                                ▸
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                </label>
              );
            })}
          </div>

          {renderError(keyName)}
        </div>
      );
    }

    case 7: {
      const groupSuffix =
        typeof groupOrdinal === "number"
          ? `_${groupOrdinal}`
          : field?.groupID
          ? `_${String(field.groupID).split("_")[1] ?? ""}`
          : String(keyName).match(/_(\d+)$/)
          ? `_${RegExp.$1}`
          : "";
      const ord = Number(groupSuffix.match(/_(\d+)/)?.[1] ?? "1");
      const disableFirstPhoneOrAccess =
        (Number(field.id) === 162 || Number(field.id) === 163) && ord === 1;
      return (
        <div
          className={`form-group ${isPadded ? "ms-3" : ""} ${
            isHalf ? "form-group--half" : ""
          }`}
        >
          <FieldLabel
            field={field}
            setInfoShowing={setInfoShowing}
            infoShowing={infoShowing}
          />
          <select
            //     id={fieldID}
            //     name={fieldID}
            id={keyName}
            name={keyName}
            className="form-input input"
            disabled={disableFirstPhoneOrAccess ?? isReadOnly}
            //     value={String(formData[fieldID] ?? "")}
            value={String(formData[keyName]) ?? ""}
            onChange={handleInputChange}
          >
            <option value="">{`Select ${getTitle(field) || "Option"}`}</option>
            {field.options?.map((opt) => (
              <option key={opt.optionId} value={String(opt.optionId)}>
                {opt.name}
              </option>
            ))}
          </select>
          {renderError(keyName)}
        </div>
      );
    }
    case 9:
      return (
        // <div className="form-group">
        <div
          className={`form-group ${isHalf ? "form-group--half" : ""} ${
            isPadded ? "ms-3" : ""
          }`}
        >
          <FieldLabel
            field={field}
            setInfoShowing={setInfoShowing}
            infoShowing={infoShowing}
          />
          <div className="relativity">
            <img
              className="addImgss"
              src="https://suyoolportalpublic.blob.core.windows.net/photos/calender.png"
              alt="Calendar"
              //       onClick={() => document.getElementById(fieldID)?.focus()}
              onClick={() => document.getElementById(String(keyName))?.focus()}
            />
            <DatePicker
              //       id={fieldID}
              //       id={String(fieldID)}
              id={String(keyName)}
              name={keyName}
              //       selected={formData[fieldID] ? new Date(formData[fieldID]) : null}
              //       selected={safeDate(formData[fieldID])}
              selected={safeDate(formData[keyName])}
              onChange={(date) =>
                handleInputChange({
                  target: {
                    //     name: fieldID,
                    name: keyName,
                    value: date ? date.toISOString().split("T")[0] : "",
                  },
                })
              }
              className="form-input input input--withIcon"
              disabled={isReadOnly}
              maxDate={new Date()}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              dateFormat="dd-MM-yyyy"
              placeholderText={field.placeHolder || "Select a date"}
            />
          </div>
          {renderError(keyName)}
        </div>
      );

    case 20:
      const setAddressBoth = (update) => {
        if (typeof update === "function") {
          setFormData((prev) => {
            const next = update(prev);
            const val = next?.[keyName];
            setmodifiedData?.((p) => ({ ...(p || {}), [keyName]: val }));
            setErrors?.((p) => (p?.[keyName] ? { ...p, [keyName]: null } : p));
            return next;
          });
        } else {
          const val = update?.[keyName];
          setmodifiedData?.((p) => ({ ...(p || {}), [keyName]: val }));
          setErrors?.((p) => (p?.[keyName] ? { ...p, [keyName]: null } : p));
          setFormData((prev) => ({ ...prev, ...update }));
        }
      };
      return (
        <div className={`form-group ${isPadded ? "ms-3" : ""}`}>
          <div className="col-6 address" style={{ position: "relative" }}>
            <AddressLable
              formData={formData}
              getTitle={getTitle}
              setFormData={setAddressBoth}
              errors={errors}
              field={{ ...field, id: keyName }}
              address={true}
            />
          </div>
        </div>
      );

    case 3: {
      // Title/section using the OLD design (FieldLabel handles tooltip UI)
      return (
        <div className={`form-title ${isPadded ? "ms-3" : ""} newTitle`}>
          <FieldLabel
            field={field} // uses field.title + field.toolTip (HTML)
            setInfoShowing={setInfoShowing} // your old toggling state
            infoShowing={infoShowing}
          />
        </div>
      );
    }
    case 4:
      return (
        <div className={`form-subtitle ${isPadded ? "ms-3" : ""} my-3`}>
          {field.title}
        </div>
      );

    case 10: {
      const ordinal = Number(groupOrdinal ?? 1);
      const key = ordinal === 1 ? fieldID : `${fieldID}_${ordinal - 1}`;
      // Fallback: if formData is empty on first load, try to read from field.value JSON array
      let initial = String(formData[keyName] ?? "");
      if (
        !initial &&
        !suppressPrefill &&
        typeof field.value === "string" &&
        field.value.trim().startsWith("[")
      ) {
        try {
          const arr = JSON.parse(field.value); // [{Value:"...", GroupID:"118_1"}, ...]
          if (Array.isArray(arr)) {
            const item = arr[ordinal - 1]; // use ordinal position as a safe heuristic
            if (item && item.Value != null) initial = String(item.Value);
          }
        } catch (_) {}
      }

      const countryList = Array.isArray(allcountries) ? allcountries : [];

      return (
        <div
          className={`form-group ${isHalf ? "form-group--half" : ""} ${
            isPadded ? "ms-3" : ""
          }`}
        >
          <FieldLabel
            field={field}
            setInfoShowing={setInfoShowing}
            infoShowing={infoShowing}
          />
          <select
            id={key}
            name={key}
            className="form-input input"
            disabled={isReadOnly}
            value={initial}
            onChange={handleInputChange}
          >
            <option value="">{field.placeHolder || "Select a country"}</option>
            {countryList.map((c) => (
              <option key={String(c.id)} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
          {renderError(keyName)}
        </div>
      );
    }

    case 21: {
      const title = getTitle(field);

      // Always bind to the cloned/suffixed key
      const v = formData[keyName];
      const hasOwn = Object.prototype.hasOwnProperty.call(formData, keyName);

      const isLogo = Number(field.id) === 117;
      const accept = isLogo ? "image/*" : "image/*,.pdf";
      const showErr = errors?.[keyName];
      const showMsg = Boolean(message?.[keyName]);
      // A) API url from field.value (only when key doesn't exist and not suppressing)
      const apiUrl =
        !hasOwn &&
        !suppressPrefill &&
        typeof field.value === "string" &&
        field.value.trim() !== ""
          ? field.value.trim()
          : "";

      // B) If FormsTemplate prefilled formData with a STRING URL, use it
      const vStr = typeof v === "string" ? v.trim() : "";

      // C) If user picked a file, we store an object with url/value
      const uploadedUrl =
        v && typeof v === "object"
          ? (typeof v.url === "string" && v.url) ||
            (typeof v.value === "string" && v.value) ||
            ""
          : "";

      // Final preview URL preference: uploaded > prefilled string > api fallback
      const previewUrl = uploadedUrl || vStr || apiUrl;

      // Derive fileName for the label
      let fileName =
        (v && typeof v === "object" && (v.file?.name || v.name)) || "";
      if (!fileName && (vStr || apiUrl)) {
        const src = vStr || apiUrl;
        try {
          fileName = new URL(src).pathname.split("/").pop() || "Uploaded file";
        } catch {
          fileName = src.split("/").pop() || "Uploaded file";
        }
      }

      // Treat as PDF only if URL clearly ends with .pdf (many signed links have no extension)
      const cleanUrl = (previewUrl || "").split("?")[0].split("#")[0];
      const isPdf = /\.pdf$/i.test(cleanUrl);
      const isImg = !!previewUrl && !isPdf;

      const placeholder = fileName || field.placeHolder || "Choose Photo";

      return (
        <div className="col-6">
          <div className={`form-group ${isPadded ? "ms-3" : ""}`}>
            <div className="form-label file-label">
              <div className="label-val">{title}</div>
            </div>

            <div className="file-upload">
              <input
                id={keyName}
                name={keyName}
                type="file"
                onChange={handleUploadChange}
                accept={accept}
                disabled={isReadOnly}
                hidden
              />

              <label
                htmlFor={keyName}
                className={`uploader ${previewUrl ? "uploader--filled" : ""}`}
              >
                <span className="uploader__icon" aria-hidden="true">
                  <img src="/Images/uploadIcon.png" alt="" />
                </span>

                {(() => {
                  const isFromUpload = v && typeof v === "object" && !!v.file; // { file, url } -> user just uploaded
                  const showFileName =
                    (isImg && isFromUpload) || // image name only when it's an upload
                    (!isImg && !!previewUrl); // always show for non-images (e.g., PDFs)

                  if (isImg) {
                    // Image path: preview always, filename only for uploads
                    return (
                      <div className="uploader__preview">
                        <img
                          src={previewUrl}
                          alt={fileName || title || "Document"}
                          className="img-doc-last-step"
                          width={200}
                          height={130}
                          loading="lazy"
                          onError={(e) =>
                            (e.currentTarget.style.display = "none")
                          }
                        />
                        {showFileName && (
                          <span className="uploader__filename">
                            {fileName || "Attached file"}
                          </span>
                        )}
                      </div>
                    );
                  }

                  // Non-image (PDF etc.): show filename/text
                  return (
                    <span className="uploader__text">
                      {fileName || "uploaded file"}
                    </span>
                  );
                })()}
              </label>
            </div>
          </div>

          {showErr && !showMsg ? (
            <div className="error" id={`error-${keyName}`}>
              {showErr}
            </div>
          ) : showMsg ? (
            <div className="text-success-upload mx-3">{message[keyName]}</div>
          ) : null}
        </div>
      );
    }

    case 22: {
      // Container parent (e.g., 118)
      const parentId = Number(field.id);
      const linkedIds = Array.isArray(field.linkedFields)
        ? field.linkedFields.map(Number)
        : [];

      // Existing clone suffixes for this parent; keep only positive integers
      const suffixes = (ownerIdxMap?.[parentId] || []).filter(
        (s) => Number(s) > 0
      );

      // Render a single group. Group 1 has no suffix; clones have numeric suffix.
      const renderGroup = (ordinal, suffix /* undefined for original */) => {
        const wrapKey =
          suffix == null ? `grp-${parentId}-1` : `grp-${parentId}-${ordinal}`;

        return (
          <div key={wrapKey} className="linked-fields">
            {/* Inside each group, render ONLY the directly-linked fields (160).
            160 will then expand its own nested fields when the user selects an option. */}
            {Number(ordinal) > 1 && typeof removeShareholder === "function" && (
              <button
                type="button"
                className="btn-remove-group remove-section-btn"
                onClick={() => removeShareholder(parentId, Number(ordinal) - 1)}
                aria-label="Remove"
                style={{
                  marginLeft: "auto",
                  position: "absolute",
                  zIndex: "10",
                  right: "304px",
                  //     position: absolute;
                  //     z-index: 10;
                  //     right: 304px;
                }}
              >
                <span>−</span>
              </button>
            )}
            {linkedIds.map((cid) => {
              const child = fieldsIndex?.[Number(cid)];
              if (!child) return null;

              return renderField({
                // Render child as a direct field (not nested-gated here)
                field: { ...child, isNestedField: false },

                // environment
                allcountries,
                formData,
                errors,
                setErrors,
                setFormData,
                handleInputChange,
                handleUploadChange,
                setModalMode,
                setModalTitle,
                setModalDes,
                setModalShow,
                infoShowing,
                setInfoShowing,
                fieldsIndex,
                countries,
                message,

                // make inputs unique per group without changing UI
                groupOrdinal: Number(ordinal),

                // clones should be empty initially (no API prefill)
                suppressPrefill: suffix != null,

                // helpers for owner groups and cleanup
                ownerIdxMap,
                addShareholder,
                removeShareholder,
                setOwnerIdxMap,
                setmodifiedData,

                // NEW: allow the radio () to render the remove button inline
                groupParentId: parentId,
                groupSuffix: suffix, // undefined for the first group
              });
            })}
          </div>
        );
      };

      return (
        <div className={`form-group ${isPadded ? "ms-3" : ""}`}>
          {/* 1) Always render one initial block, even if API is empty */}
          {renderGroup(1, undefined)}

          {/* 2) Render cloned blocks */}
          {suffixes.map((sfx) => renderGroup(sfx + 1, sfx))}

          {/* 3) Add button creates another empty block that shows ONLY 160 at first */}
          <div className="linked-fields-add">
            <button
              type="button"
              className="add-section-btn"
              onClick={() => addShareholder(parentId, linkedIds)}
            >
              {field.placeHolder || "+ Add"}
            </button>
          </div>

          {renderError(keyName)}
        </div>
      );
    }

    case 23:
      return (
        <div className={`form-group ${isHalf ? "form-group--half" : ""}`}>
          <FieldLabel
            field={field}
            setInfoShowing={setInfoShowing}
            infoShowing={infoShowing}
          />
          <input
            //     id={fieldID}
            //     name={fieldID}
            className="form-input input"
            disabled={isReadOnly}
            type="text"
            placeholder={field.placeHolder || ""}
            value={
              formData[keyName]
                ? Number(formData[keyName]).toLocaleString()
                : ""
            }
            onChange={(e) => {
              const raw = e.target.value.replace(/\D/g, "");
              setFormData((prev) => ({ ...prev, [keyName]: raw }));
              setmodifiedData?.((prev) => ({ ...prev, [keyName]: raw }));
              setErrors?.((prev) =>
                prev?.[keyName] ? { ...prev, [keyName]: null } : prev
              );
            }}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
            }}
            inputMode="numeric"
            maxLength={field.length || 20}
            pattern="[0-9]*"
          />
          {renderError(keyName)}
        </div>
      );

    case 24: {
      // Phone number with per-group error keys
      const rawVal = String(formData[keyName] ?? "");

      // 1) Figure out a stable group suffix
      // Prefer explicit props if you have them (groupOrdinal / groupID on the field),
      // otherwise fall back to parsing from keyName like "126_1".

      const groupSuffix =
        typeof groupOrdinal === "number"
          ? `_${groupOrdinal}`
          : field?.groupID
          ? `_${String(field.groupID).split("_")[1] ?? ""}`
          : String(keyName).match(/_(\d+)$/)
          ? `_${RegExp.$1}`
          : "";
      const ord = Number(groupSuffix.match(/_(\d+)/)?.[1] ?? "1");
      const disableFirstPhoneOrAccess =
        (Number(field.id) === 162 || Number(field.id) === 163) && ord === 1;
      // 2) Build the error key = baseId + group suffix
      const baseId = String(field.id ?? field.fieldId ?? keyName);
      const errorKey = `${baseId}${groupSuffix}`;

      const normalize = (s) => {
        const plus = s.startsWith("+") ? "+" : "";
        const digits = s.replace(/^\+/, "").replace(/\D/g, "");
        return plus + digits;
      };

      const validatePhone = (s) => {
        const digitsOnly = s.replace(/^\+/, "").replace(/\D/g, "");
        if (digitsOnly.length > 0 && digitsOnly.length < 7) {
          return "Enter a valid phone number";
        }
        return null;
      };

      const setErr = (msg) =>
        setErrors?.((prev) => {
          const p = { ...(prev || {}) };
          if (msg) p[errorKey] = msg;
          else if (p[errorKey]) delete p[errorKey];
          return p;
        });

      const handlePhoneChange = (e) => {
        const next = normalize(e.target.value);
        setFormData((p) => ({ ...p, [keyName]: next }));
        setmodifiedData?.((p) => ({ ...p, [keyName]: next }));
        setErr(validatePhone(next));
      };

      const handlePhoneBlur = (e) => {
        const next = normalize(e.target.value);
        setFormData((p) => ({ ...p, [keyName]: next }));
        setmodifiedData?.((p) => ({ ...p, [keyName]: next }));
        setErr(validatePhone(next));
      };

      const localError = errors?.[errorKey] ?? null;

      return (
        <div
          className={`form-group ${isPadded ? "ms-3" : ""} ${
            isHalf ? "form-group--half" : ""
          }`}
        >
          <FieldLabel
            field={field}
            setInfoShowing={setInfoShowing}
            infoShowing={infoShowing}
          />

          <input
            id={keyName}
            name={keyName}
            className={`form-input input ${isHalf ? "input--full" : ""}`}
            type="tel"
            placeholder={field.placeHolder || "+961"}
            disabled={disableFirstPhoneOrAccess ?? isReadOnly}
            value={rawVal}
            inputMode="tel"
            maxLength={field.length || 15}
            onChange={handlePhoneChange}
            onBlur={handlePhoneBlur}
          />

          {/* Prefer showing the per-group error here */}
          {localError && (
            <div
              className="form-error"
              id={`error-${keyName}`}
              style={{ marginTop: 4 }}
            >
              {localError}
            </div>
          )}
        </div>
      );
    }

    default:
      return null;
  }
};

const renderFieldCompany = ({
  field,
  formData,
  errors,
  setErrors,
  setFormData,
  handleInputChange,
  setModalMode,
  setModalTitle,
  setModalDes,
  setModalShow,
  infoShowing,
  setInfoShowing,
}) => {
  const fieldID = String(field.id ?? field.fieldId);
  const errorDisplay = errors?.[fieldID] && (
    <div className="error">{errors[fieldID]}</div>
  );

  const options = Array.isArray(field.options) ? field.options : [];
  const rawValue = field.value;

  const resolveOptionName = (val) => {
    const id = Number(val);
    const opt = options.find((o) => Number(o.optionId) === id);
    return opt ? opt.name : String(val);
  };

  const displayValue =
    options.length > 0
      ? Array.isArray(rawValue)
        ? rawValue.map(resolveOptionName).join(", ")
        : resolveOptionName(rawValue)
      : rawValue ?? "";

  if (field.type == 3) {
    return <div className="form-title">{field.displayText}</div>;
  }
  if (field.type == 4) {
    return <div className="form-subtitle">{field.displayText}</div>;
  }

  return (
    <div className="form-field">
      <p className="key">{field.displayText}</p>
      <p className="value">
        {(() => {
          if (Number(field.type) === 9) {
            const v = String(displayValue ?? "").trim();

            const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (m) {
              const [, y, mm, dd] = m;
              return `${dd}-${mm}-${y}`; // dd-MM-yyyy
            }

            if (/^\d{2}-\d{2}-\d{4}$/.test(v)) return v;

            return v;
          }
          // Not a date, return as text
          return displayValue;
        })()}
      </p>
      {errorDisplay}
    </div>
  );
};

const renderFieldCompanyImages = ({ field, errors }) => {
  const fieldID = field.id ?? field.fieldId;
  const errorDisplay = errors?.[fieldID] && (
    <div className="error">{errors[fieldID]}</div>
  );
  return (
    <div className="img-wrapper mt-4" key={fieldID}>
      {/* <img src={field?.pictureUrl} className="my-2 img-doc-last-step" /> */}
      <a
        className="name-img "
        href={field?.pictureUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {field?.name}
      </a>

      {errorDisplay}
    </div>
  );
};

export { renderField, renderFieldCompany, renderFieldCompanyImages };
